import React, { useState, useEffect, useMemo } from "react";
import "./WorkboardsPage.css";
import api from '../../api';
import TaskModal from '../Editor/TaskModal';
import Tooltip from '../Editor/Tooltip';
import { useAuth } from '../../contexts/AuthContext';
import SprintManagementModal from './SprintManagementModal';
import BurndownChart from './BurndownChart';

export default function WorkboardsPage({ canEdit = false }) {
  const { isAuthenticated, user } = useAuth();
  const [userBoardAccess, setUserBoardAccess] = useState([]);
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskTypes, setTaskTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [boardStages, setBoardStages] = useState([]);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    type: "",
    priority: "",
    status: "",
    requester: "",
    email: "",
    description: "",
  });
  const [showAddRequesterModal, setShowAddRequesterModal] = useState(false);
  const [newRequester, setNewRequester] = useState({ name: "", email: "" });
  const [requesterError, setRequesterError] = useState("");
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [hasBoardAdminAccess, setHasBoardAdminAccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssigneeFilter, setSelectedAssigneeFilter] = useState(null); // Will be set based on admin access
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null); // null, "New", "In Progress", "Queue", or status string
  const [selectedStageFilter, setSelectedStageFilter] = useState(null); // null or stageId
  const [tasksWithSubtasks, setTasksWithSubtasks] = useState({}); // Map of taskId -> subtasks array
  const [tasksWithAssignees, setTasksWithAssignees] = useState({}); // Map of taskId -> assignees array
  const [boardSettings, setBoardSettings] = useState({ 
    burndown_chart_enabled: false,
    visible_columns: ['title', 'type', 'priority', 'status', 'requester', 'team']
  });
  const [activeSprint, setActiveSprint] = useState(null);
  const [sprintTasks, setSprintTasks] = useState([]);
  const [showBurndownChart, setShowBurndownChart] = useState(false);
  const [sprints, setSprints] = useState([]); // All sprints for the board
  const [taskSprints, setTaskSprints] = useState({}); // Map of taskId -> sprintId

  // Fetch boards and dropdown options on mount
  useEffect(() => {
    fetchBoards();
    fetchDropdownOptions();
    if (isAuthenticated) {
      fetchUserBoardAccess();
    }
  }, [isAuthenticated]);

  // Refresh board settings when window regains focus (in case settings were updated in another tab/modal)
  useEffect(() => {
    const handleFocus = () => {
      if (selectedBoard?.id) {
        fetchBoardSettings(selectedBoard.id);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [selectedBoard]);

  // Fetch user's board access permissions
  const fetchUserBoardAccess = async () => {
    try {
      const res = await api.get('/auth/resource-access');
      const boardAccess = (res.data || []).filter(a => a.resource_type === 'board');
      setUserBoardAccess(boardAccess);
    } catch (err) {
      console.error('Failed to fetch user board access:', err);
    }
  };

  // Check if user has admin access to selected board (admin only, not edit)
  useEffect(() => {
    if (selectedBoard && userBoardAccess.length > 0) {
      const hasAccess = userBoardAccess.some(access => {
        const isAdmin = access.access_level === 'admin';
        const matchesBoard = !access.resource_id || access.resource_id === selectedBoard.id;
        return isAdmin && matchesBoard;
      });
      setHasBoardAdminAccess(hasAccess);
    } else {
      setHasBoardAdminAccess(false);
    }
  }, [selectedBoard, userBoardAccess]);

  // Check if user has edit access to the selected board
  const hasBoardEditAccess = () => {
    if (!selectedBoard || !isAuthenticated || userBoardAccess.length === 0) return false;
    
    const accessLevels = { view: 1, edit: 2, admin: 3 };
    
    // Check for type-level edit/admin access (all boards)
    const typeAccess = userBoardAccess.find(a => 
      a.resource_type === 'board' && (a.resource_id === null || a.resource_id === undefined)
    );
    if (typeAccess && accessLevels[typeAccess.access_level] >= accessLevels['edit']) {
      return true;
    }
    
    // Check for specific board edit/admin access
    const boardAccess = userBoardAccess.find(a => 
      a.resource_type === 'board' && a.resource_id === selectedBoard.id
    );
    if (boardAccess && accessLevels[boardAccess.access_level] >= accessLevels['edit']) {
      return true;
    }
    
    return false;
  };

  // Determine if user can edit based on selected board access
  const canEditCurrentBoard = hasBoardEditAccess();

  // Fetch dropdown options (non-board-specific)
  const fetchDropdownOptions = async () => {
    try {
      const [prioritiesRes, statusesRes, peopleRes, teamsRes] = await Promise.all([
        api.get('/tasks/priorities'),
        api.get('/tasks/statuses'),
        api.get('/tasks/people'),
        api.get('/tasks/teams')
      ]);
      setPriorities(prioritiesRes.data || []);
      setStatuses(statusesRes.data || []);
      setPeople(peopleRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (err) {
      console.error("Failed to fetch dropdown options:", err);
    }
  };

  // Fetch board-specific task types
  const fetchBoardTaskTypes = async (boardId) => {
    if (!boardId) {
      setTaskTypes([]);
      return;
    }
    try {
      const res = await api.get(`/board-settings/${boardId}/types`);
      setTaskTypes(res.data || []);
    } catch (err) {
      console.error("Failed to fetch board task types:", err);
      setTaskTypes([]);
    }
  };

  // Fetch board-specific stages
  const fetchBoardStages = async (boardId) => {
    if (!boardId) {
      setBoardStages([]);
      return;
    }
    try {
      const res = await api.get(`/board-settings/${boardId}/stages`);
      setBoardStages(res.data || []);
    } catch (err) {
      console.error("Failed to fetch board stages:", err);
      setBoardStages([]);
    }
  };

  // Check if user has access to a specific board
  const hasBoardAccess = (boardId) => {
    if (!isAuthenticated) return false;
    
    // Check for type-level access (all boards)
    const typeAccess = userBoardAccess.find(a => 
      a.resource_type === 'board' && (a.resource_id === null || a.resource_id === undefined)
    );
    if (typeAccess) return true;
    
    // Check for specific board access
    const boardAccess = userBoardAccess.find(a => 
      a.resource_type === 'board' && a.resource_id === boardId
    );
    return !!boardAccess;
  };

  const fetchBoards = async () => {
    try {
      setLoading(true);
      const res = await api.get('/boards');
      const allBoards = Array.isArray(res.data) ? res.data : [];
      
      // Filter boards based on user access
      const accessibleBoards = isAuthenticated 
        ? allBoards.filter(board => hasBoardAccess(board.id))
        : [];
      
      setBoards(accessibleBoards);
      
      // Set default to first accessible board
      if (accessibleBoards.length > 0) {
        const designBoard = accessibleBoards.find(b => 
          b.name.toLowerCase().includes('design') || 
          b.team_name?.toLowerCase().includes('design')
        );
        setSelectedBoard(designBoard || accessibleBoards[0]);
      }
    } catch (err) {
      console.error("Failed to fetch boards:", err);
    } finally {
      setLoading(false);
    }
  };

  // Refetch boards when user board access changes
  useEffect(() => {
    if (isAuthenticated && userBoardAccess.length >= 0) {
      fetchBoards();
    }
  }, [userBoardAccess]);

  // Fetch tasks and board settings when selected board changes
  useEffect(() => {
    if (selectedBoard?.team_id) {
      fetchTasksForTeam(selectedBoard.team_id);
      fetchBoardSettings(selectedBoard.id);
      fetchActiveSprint(selectedBoard.id);
      fetchSprintsForBoard(selectedBoard.id);
      fetchBoardTaskTypes(selectedBoard.id);
      fetchBoardStages(selectedBoard.id);
    } else {
      setTasks([]);
      setBoardSettings({ 
        burndown_chart_enabled: false,
        visible_columns: ['title', 'type', 'priority', 'status', 'requester', 'team']
      });
      setActiveSprint(null);
      setSprintTasks([]);
      setSprints([]);
      setTaskSprints({});
      setTaskTypes([]);
      setBoardStages([]);
      setTasksWithSubtasks({});
      setTasksWithAssignees({});
    }
    // Clear search and reset filters when board changes
    setSearchQuery("");
    // Set default filter based on admin access
    setSelectedAssigneeFilter(hasBoardAdminAccess ? "all" : "me");
    setSelectedStatusFilter(null);
    setSelectedStageFilter(null);
    setShowBurndownChart(false);
  }, [selectedBoard, hasBoardAdminAccess]);

  // Fetch board settings
  const fetchBoardSettings = async (boardId) => {
    try {
      const res = await api.get(`/board-settings/${boardId}/settings`);
      const settings = res.data || { 
        burndown_chart_enabled: false,
        visible_columns: ['title', 'type', 'priority', 'status', 'requester', 'team']
      };
      // Ensure visible_columns is an array
      if (!Array.isArray(settings.visible_columns)) {
        settings.visible_columns = ['title', 'type', 'priority', 'status', 'requester', 'team'];
      }
      setBoardSettings(settings);
    } catch (err) {
      console.error('Failed to fetch board settings:', err);
      setBoardSettings({ 
        burndown_chart_enabled: false,
        visible_columns: ['title', 'type', 'priority', 'status', 'requester', 'team']
      });
    }
  };

  // Fetch active sprint for the board
  const fetchActiveSprint = async (boardId) => {
    try {
      const res = await api.get(`/sprints/board/${boardId}/active`);
      if (res.data) {
        setActiveSprint(res.data);
        fetchSprintTasks(res.data.id);
      } else {
        setActiveSprint(null);
        setSprintTasks([]);
      }
    } catch (err) {
      // 404 is expected if no active sprint exists
      if (err.response?.status === 404) {
        setActiveSprint(null);
        setSprintTasks([]);
      } else {
        console.error('Failed to fetch active sprint:', err);
        setActiveSprint(null);
        setSprintTasks([]);
      }
    }
  };

  // Fetch tasks for a sprint
  const fetchSprintTasks = async (sprintId) => {
    try {
      const res = await api.get(`/sprints/${sprintId}/tasks`);
      setSprintTasks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch sprint tasks:', err);
      setSprintTasks([]);
    }
  };

  // Fetch all sprints for the board
  const fetchSprintsForBoard = async (boardId) => {
    try {
      const res = await api.get(`/sprints/board/${boardId}`);
      setSprints(res.data || []);
    } catch (err) {
      console.error('Failed to fetch sprints:', err);
      setSprints([]);
    }
  };

  // Load sprint assignments for all tasks
  const loadTaskSprints = async (taskIds) => {
    if (!taskIds || taskIds.length === 0) {
      setTaskSprints({});
      return;
    }

    try {
      const sprintPromises = taskIds.map(async (taskId) => {
        try {
          const res = await api.get(`/sprints/task/${taskId}`);
          return { taskId, sprint: res.data };
        } catch (err) {
          // 404 is expected if task has no sprint
          if (err.response?.status !== 404) {
            console.error(`Failed to fetch sprint for task ${taskId}:`, err);
          }
          return { taskId, sprint: null };
        }
      });

      const results = await Promise.all(sprintPromises);
      const sprintMap = {};
      results.forEach(({ taskId, sprint }) => {
        if (sprint) {
          sprintMap[taskId] = sprint.id;
        }
      });
      setTaskSprints(sprintMap);
    } catch (err) {
      console.error('Failed to load task sprints:', err);
      setTaskSprints({});
    }
  };

  const fetchTasksForTeam = async (teamId) => {
    try {
      const res = await api.get('/tasks');
      const allTasks = Array.isArray(res.data) ? res.data : [];
      // Filter tasks by team_id
      const teamTasks = allTasks.filter(task => task.team_id === teamId);
      setTasks(teamTasks);
      
      // Load sprint assignments for these tasks
      const taskIds = teamTasks.map(t => t.id);
      await loadTaskSprints(taskIds);
      
      // Fetch subtasks and assignees for all tasks (for filtering)
      await Promise.all([
        loadSubtasksForTasks(taskIds),
        loadTaskAssignees(taskIds)
      ]);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
      setTasks([]);
      setTaskSprints({});
      setTasksWithSubtasks({});
      setTasksWithAssignees({});
    }
  };

  // Load subtasks for multiple tasks
  const loadSubtasksForTasks = async (taskIds) => {
    if (!taskIds || taskIds.length === 0) {
      setTasksWithSubtasks({});
      return;
    }

    try {
      const subtaskPromises = taskIds.map(async (taskId) => {
        try {
          const res = await api.get(`/subtasks/task/${taskId}`);
          return { taskId, subtasks: res.data || [] };
        } catch (err) {
          // 404 is expected if task has no subtasks
          if (err.response?.status !== 404) {
            console.error(`Failed to fetch subtasks for task ${taskId}:`, err);
          }
          return { taskId, subtasks: [] };
        }
      });

      const results = await Promise.all(subtaskPromises);
      const subtasksMap = {};
      results.forEach(({ taskId, subtasks }) => {
        subtasksMap[taskId] = subtasks;
      });
      setTasksWithSubtasks(subtasksMap);
    } catch (err) {
      console.error("Failed to load subtasks:", err);
      setTasksWithSubtasks({});
    }
  };

  // Load task assignees for multiple tasks
  const loadTaskAssignees = async (taskIds) => {
    if (!taskIds || taskIds.length === 0) {
      setTasksWithAssignees({});
      return;
    }

    try {
      const assigneePromises = taskIds.map(async (taskId) => {
        try {
          const res = await api.get(`/assignees/task/${taskId}`);
          return { taskId, assignees: res.data || [] };
        } catch (err) {
          // 404 is expected if task has no assignees
          if (err.response?.status !== 404) {
            console.error(`Failed to fetch assignees for task ${taskId}:`, err);
          }
          return { taskId, assignees: [] };
        }
      });

      const results = await Promise.all(assigneePromises);
      const assigneesMap = {};
      results.forEach(({ taskId, assignees }) => {
        assigneesMap[taskId] = assignees;
      });
      setTasksWithAssignees(assigneesMap);
    } catch (err) {
      console.error("Failed to load task assignees:", err);
      setTasksWithAssignees({});
    }
  };

  const handleTitleKeyPress = async (e) => {
    if (e.key === 'Enter' && newTask.title.trim()) {
      e.preventDefault();
      e.stopPropagation();
      await addNewTask(e);
    }
  };

  const handleAddRequester = async (e) => {
    e.preventDefault();
    setRequesterError("");

    try {
      const res = await api.post('/tasks/people', {
        name: newRequester.name,
        emailAddress: newRequester.email
      });

      // Refresh people list
      const peopleRes = await api.get('/tasks/people');
      setPeople(peopleRes.data || []);

      // Update newTask with the new requester
      setNewTask({
        ...newTask,
        requester: res.data.name,
        email: res.data.emailAddress
      });

      // Close modal and reset
      setShowAddRequesterModal(false);
      setNewRequester({ name: "", email: "" });
    } catch (err) {
      if (err.response?.status === 409) {
        setRequesterError("A requester with this email already exists");
      } else {
        setRequesterError(err.response?.data?.error || "Failed to create requester");
      }
    }
  };

  const addNewTask = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const trimmedTitle = newTask.title?.trim();
    if (!trimmedTitle) {
      console.log("❌ Cannot submit: title is empty");
      return;
    }

    if (!selectedBoard?.team_id) {
      alert("Please select a board first");
      return;
    }

    const payload = {
      title: trimmedTitle,
      description: newTask.description || null,
      type: newTask.type || null,
      priority: newTask.priority || null,
      status: newTask.status || "New", // Default to "New"
      personName: newTask.requester || null,
      personEmail: newTask.email || null,
      team_id: selectedBoard.team_id, // Always use the selected board's team
    };

    try {
      const res = await api.post('/tasks', payload);
      const saved = res.data;

      if (!res.status.toString().startsWith('2')) {
        alert(saved.error || "Failed to save task");
        return;
      }

      // Add to tasks list if it matches the current board's team
      if (saved.team_id === selectedBoard.team_id) {
        setTasks([...tasks, saved]);
        // Refresh sprint tasks if there's an active sprint (new task might be added to sprint)
        if (activeSprint) {
          fetchSprintTasks(activeSprint.id);
        }
      }

      // Reset form but keep it open for quick additions
      setNewTask({
        ...newTask,
        title: "",
      });
    } catch (err) {
      console.error("Failed to add task:", err);
      const errorMessage = err.response?.data?.error 
        || err.response?.data?.details 
        || err.message 
        || 'Failed to add task. Please try again.';
      alert(errorMessage);
    }
  };

  // Helper to get current stage for a task based on its status
  const getCurrentStageForTask = (task) => {
    if (!task.status || boardStages.length === 0) return null;
    // Find stage that maps to this task's status
    const stage = boardStages.find(s => s.status === task.status);
    return stage ? stage.id : null;
  };

  // Helper to get status from stage
  const getStatusFromStage = (stageId) => {
    if (!stageId) return null;
    const stage = boardStages.find(s => s.id === parseInt(stageId, 10));
    return stage?.status || null;
  };

  const handleFieldUpdate = async (taskId, field, value) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Optimistically update the UI immediately (don't await)
    let updatedTask = { ...task };
    
    if (field === 'requester') {
      updatedTask.personName = value || null;
      updatedTask.requester = value || null;
    } else if (field === 'team') {
      const selectedTeam = teams.find(t => t.name === value);
      updatedTask.teamName = value || null;
      updatedTask.team = value || null;
      updatedTask.team_id = selectedTeam?.id || null;
    } else if (field === 'stage') {
      // Map stage to status
      const mappedStatus = getStatusFromStage(value);
      updatedTask.status = mappedStatus || null;
    } else {
      updatedTask[field] = value || null;
    }
    
    // Update state immediately for instant UI feedback
    setTasks(prevTasks => prevTasks.map(t => 
      t.id === taskId ? updatedTask : t
    ));

    // Then update the backend asynchronously (fire and forget)
    (async () => {
      try {
      const selectedTeam = field === 'team' ? teams.find(t => t.name === value) : null;
      const selectedPerson = field === 'requester' ? people.find(p => p.name === value) : null;
      
      // If field is 'stage', map it to status
      let statusValue = task.status;
      if (field === 'stage') {
        statusValue = getStatusFromStage(value);
      } else if (field === 'status') {
        statusValue = value || null;
      }
      
      const payload = {
        title: task.title,
        description: task.description,
        type: field === 'type' ? (value || null) : task.type,
        priority: field === 'priority' ? (value || null) : task.priority,
        status: statusValue,
        personName: field === 'requester' ? (value || null) : (task.personName || task.requester),
        personEmail: field === 'requester' ? (selectedPerson?.emailAddress || task.personEmail || task.email) : (task.personEmail || task.email),
        team_id: field === 'team' ? (selectedTeam?.id || null) : (task.team_id || null),
      };

        const res = await api.put(`/tasks/${taskId}`, payload);
        // Update with server response to ensure consistency
        const updatedTaskFromServer = res.data;
        setTasks(prevTasks => prevTasks.map(t => 
          t.id === taskId ? updatedTaskFromServer : t
        ));
        
        // If team changed, remove task from current board if it no longer matches
        if (field === 'team' && selectedBoard?.team_id && updatedTaskFromServer.team_id !== selectedBoard.team_id) {
          setTasks(prevTasks => prevTasks.filter(t => t.id !== taskId));
        }

        // Refresh sprint tasks if there's an active sprint (status changes affect burndown)
        if (activeSprint && field === 'status') {
          fetchSprintTasks(activeSprint.id);
        }
      } catch (err) {
        console.error("Failed to update task:", err);
        // Revert optimistic update on error
        setTasks(prevTasks => prevTasks.map(t => 
          t.id === taskId ? task : t
        ));
        alert('Failed to update task. Please try again.');
      }
    })();
  };

  // Handle sprint change for a task
  const handleSprintChange = async (taskId, sprintId) => {
    const currentSprintId = taskSprints[taskId];
    
    // If selecting the same sprint, do nothing
    if (currentSprintId === parseInt(sprintId)) {
      return;
    }

    try {
      // Remove from current sprint if exists
      if (currentSprintId) {
        try {
          await api.delete(`/sprints/${currentSprintId}/tasks/${taskId}`);
        } catch (err) {
          // Ignore 404 errors (task might not be in sprint)
          if (err.response?.status !== 404) {
            throw err;
          }
        }
      }

      // Add to new sprint if selected
      if (sprintId) {
        await api.post(`/sprints/${sprintId}/tasks`, {
          task_id: taskId
        });
      }

      // Update local state
      const newTaskSprints = { ...taskSprints };
      if (sprintId) {
        newTaskSprints[taskId] = parseInt(sprintId);
      } else {
        delete newTaskSprints[taskId];
      }
      setTaskSprints(newTaskSprints);

      // Refresh sprint tasks if there's an active sprint
      if (activeSprint) {
        fetchSprintTasks(activeSprint.id);
      }
    } catch (err) {
      console.error("Failed to update task sprint:", err);
      alert('Failed to update sprint assignment. Please try again.');
    }
  };

  // Filter tasks by search query and assignee
  // Must be before any conditional returns to follow React hooks rules
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    // If no filter is set yet, default based on admin access
    const effectiveFilter = selectedAssigneeFilter || (hasBoardAdminAccess ? 'all' : 'me');
    
    // Filter by assignee (tasks and subtasks)
    if (effectiveFilter && effectiveFilter !== 'all') {
      const currentUserId = user?.id ? parseInt(user.id, 10) : null;
      
      if (effectiveFilter === 'me' && currentUserId) {
        // Show tasks assigned to current user OR tasks with subtasks assigned to current user
        filtered = tasks.filter(task => {
          const taskAssignees = tasksWithAssignees[task.id] || [];
          const hasAssignedTask = taskAssignees.some(a => a.person_id === currentUserId);
          
          const taskSubtasks = tasksWithSubtasks[task.id] || [];
          const hasAssignedSubtask = taskSubtasks.some(subtask => 
            subtask.assignees && subtask.assignees.some(a => a.person_id === currentUserId)
          );
          
          return hasAssignedTask || hasAssignedSubtask;
        });
      } else if (effectiveFilter === 'unassigned') {
        // Show tasks with no assignees AND no subtasks with assignees
        filtered = tasks.filter(task => {
          const taskAssignees = tasksWithAssignees[task.id] || [];
          const hasAssignedTask = taskAssignees.length > 0;
          
          const taskSubtasks = tasksWithSubtasks[task.id] || [];
          const hasAssignedSubtask = taskSubtasks.some(subtask => 
            subtask.assignees && subtask.assignees.length > 0
          );
          
          return !hasAssignedTask && !hasAssignedSubtask;
        });
      } else {
        // Filter by specific person ID
        const personId = parseInt(effectiveFilter, 10);
        if (!isNaN(personId)) {
          filtered = tasks.filter(task => {
            const taskAssignees = tasksWithAssignees[task.id] || [];
            const hasAssignedTask = taskAssignees.some(a => a.person_id === personId);
            
            const taskSubtasks = tasksWithSubtasks[task.id] || [];
            const hasAssignedSubtask = taskSubtasks.some(subtask => 
              subtask.assignees && subtask.assignees.some(a => a.person_id === personId)
            );
            
            return hasAssignedTask || hasAssignedSubtask;
          });
        }
      }
    }
    
    // Filter by status
    if (selectedStatusFilter) {
      filtered = filtered.filter(task => task.status === selectedStatusFilter);
    }
    
    // Filter by stage (if stages are configured)
    if (selectedStageFilter && boardStages.length > 0) {
      const stage = boardStages.find(s => s.id === parseInt(selectedStageFilter, 10));
      if (stage && stage.status) {
        filtered = filtered.filter(task => task.status === stage.status);
      }
    }
    
    // Filter by search query (searches title and description)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => {
        const titleMatch = task.title?.toLowerCase().includes(query);
        const descriptionMatch = task.description?.toLowerCase().includes(query);
        return titleMatch || descriptionMatch;
      });
    }
    
    return filtered;
  }, [tasks, searchQuery, selectedAssigneeFilter, selectedStatusFilter, selectedStageFilter, tasksWithSubtasks, tasksWithAssignees, boardStages, user, hasBoardAdminAccess]);

  if (loading) {
    return (
      <div className="workboards-page">
        <h2>Workboards</h2>
        <div className="loading">Loading boards...</div>
      </div>
    );
  }

  // Get tasks for the current filter (for counting)
  const getTasksForCurrentFilter = () => {
    const currentUserId = user?.id ? parseInt(user.id, 10) : null;
    let filtered = tasks;
    
    // If no filter is set yet, default based on admin access
    const effectiveFilter = selectedAssigneeFilter || (hasBoardAdminAccess ? 'all' : 'me');
    
    // Filter by assignee
    if (effectiveFilter && effectiveFilter !== 'all') {
      if (effectiveFilter === 'me' && currentUserId) {
        filtered = tasks.filter(task => {
          const taskAssignees = tasksWithAssignees[task.id] || [];
          const hasAssignedTask = taskAssignees.some(a => a.person_id === currentUserId);
          const taskSubtasks = tasksWithSubtasks[task.id] || [];
          const hasAssignedSubtask = taskSubtasks.some(subtask => 
            subtask.assignees && subtask.assignees.some(a => a.person_id === currentUserId)
          );
          return hasAssignedTask || hasAssignedSubtask;
        });
      } else if (effectiveFilter === 'unassigned') {
        filtered = tasks.filter(task => {
          const taskAssignees = tasksWithAssignees[task.id] || [];
          const hasAssignedTask = taskAssignees.length > 0;
          const taskSubtasks = tasksWithSubtasks[task.id] || [];
          const hasAssignedSubtask = taskSubtasks.some(subtask => 
            subtask.assignees && subtask.assignees.length > 0
          );
          return !hasAssignedTask && !hasAssignedSubtask;
        });
      } else {
        const personId = parseInt(effectiveFilter, 10);
        if (!isNaN(personId)) {
          filtered = tasks.filter(task => {
            const taskAssignees = tasksWithAssignees[task.id] || [];
            const hasAssignedTask = taskAssignees.some(a => a.person_id === personId);
            const taskSubtasks = tasksWithSubtasks[task.id] || [];
            const hasAssignedSubtask = taskSubtasks.some(subtask => 
              subtask.assignees && subtask.assignees.some(a => a.person_id === personId)
            );
            return hasAssignedTask || hasAssignedSubtask;
          });
        }
      }
    }
    
    return filtered;
  };

  // Calculate status counts for filtered tasks
  const getStatusCounts = () => {
    const filteredTasks = getTasksForCurrentFilter();
    if (!selectedBoard || filteredTasks.length === 0) {
      return { 'New': 0, 'In Progress': 0, 'Queue': 0 };
    }
    
    const counts = { 'New': 0, 'In Progress': 0, 'Queue': 0 };
    filteredTasks.forEach(task => {
      const status = task.status || '';
      if (status === 'New') {
        counts['New'] = (counts['New'] || 0) + 1;
      } else if (status === 'In Progress') {
        counts['In Progress'] = (counts['In Progress'] || 0) + 1;
      } else if (status === 'Queue') {
        counts['Queue'] = (counts['Queue'] || 0) + 1;
      }
    });
    
    return counts;
  };

  // Calculate stage counts for filtered tasks
  const getStageCounts = () => {
    const filteredTasks = getTasksForCurrentFilter();
    if (!selectedBoard || boardStages.length === 0 || filteredTasks.length === 0) {
      return {};
    }
    
    const counts = {};
    boardStages.forEach(stage => {
      counts[stage.id] = 0;
    });
    
    filteredTasks.forEach(task => {
      // Find stage that maps to this task's status
      const stage = boardStages.find(s => s.status === task.status);
      if (stage) {
        counts[stage.id] = (counts[stage.id] || 0) + 1;
      }
    });
    
    return counts;
  };

  const statusCounts = getStatusCounts();
  const stageCounts = getStageCounts();

  return (
    <div className="workboards-page">
      <div className="page-header-row">
        <h2>Workboards</h2>
        {boards.length > 0 && (
          <div className="board-selector-dropdown">
            <label htmlFor="board-select">Board:</label>
            <select
              id="board-select"
              className="board-select"
              value={selectedBoard?.id || ''}
              onChange={(e) => {
                const boardId = parseInt(e.target.value);
                const board = boards.find(b => b.id === boardId);
                setSelectedBoard(board || null);
              }}
            >
              <option value="">Select a board...</option>
              {boards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name} {board.team_name ? `(${board.team_name})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {boards.length === 0 ? (
        <div className="no-boards">
          <p>No boards available. Create a team and board to get started.</p>
        </div>
      ) : (
        <div className="workboards-container">

          {/* Selected Board View */}
          {selectedBoard && (
            <div className="board-view">
              <div className="board-header">
                <div className="board-header-left">
                  <div className="board-title-container">
                    <h3>{selectedBoard.name}</h3>
                    {selectedBoard.team_name && (
                      <span className="board-team-badge">{selectedBoard.team_name}</span>
                    )}
                  </div>
                  <div className="board-search">
                    <input
                      type="text"
                      placeholder="Search tasks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="board-search-input"
                    />
                  </div>
                </div>
                <div className="board-header-right">
                  {boardSettings.burndown_chart_enabled && activeSprint && (
                    <button
                      className="sprint-management-btn"
                      onClick={() => setShowBurndownChart(!showBurndownChart)}
                      title={showBurndownChart ? "Hide Burndown Chart" : "Show Burndown Chart"}
                    >
                      {showBurndownChart ? "Hide Chart" : "Show Chart"}
                    </button>
                  )}
                  {hasBoardAdminAccess && (
                    <button
                      className="sprint-management-btn"
                      onClick={() => setShowSprintModal(true)}
                      title="Manage Sprints"
                    >
                      Manage Sprints
                    </button>
                  )}
                </div>
              </div>
              {selectedBoard.description && (
                <p className="board-description">{selectedBoard.description}</p>
              )}

              {/* Reporting Section */}
              <div className="board-reporting-section">
                <h4>Status Counts</h4>
                {/* Status Counts - Clickable Filters */}
                {canEditCurrentBoard && (
                  <div className="status-counts">
                    <div 
                      className={`status-count-item ${selectedStatusFilter === 'New' ? 'active' : ''}`}
                      onClick={() => {
                        if (selectedStatusFilter === 'New') {
                          setSelectedStatusFilter(null);
                        } else {
                          setSelectedStatusFilter('New');
                          setSelectedStageFilter(null); // Clear stage filter when selecting status
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="status-name">New</span>
                      <span className="status-number">{statusCounts['New'] || 0}</span>
                    </div>
                    <div 
                      className={`status-count-item ${selectedStatusFilter === 'Queue' ? 'active' : ''}`}
                      onClick={() => {
                        if (selectedStatusFilter === 'Queue') {
                          setSelectedStatusFilter(null);
                        } else {
                          setSelectedStatusFilter('Queue');
                          setSelectedStageFilter(null);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="status-name">Queue</span>
                      <span className="status-number">{statusCounts['Queue'] || 0}</span>
                    </div>
                    <div 
                      className={`status-count-item ${selectedStatusFilter === 'In Progress' ? 'active' : ''}`}
                      onClick={() => {
                        if (selectedStatusFilter === 'In Progress') {
                          setSelectedStatusFilter(null);
                        } else {
                          setSelectedStatusFilter('In Progress');
                          setSelectedStageFilter(null);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="status-name">In Progress</span>
                      <span className="status-number">{statusCounts['In Progress'] || 0}</span>
                    </div>
                  </div>
                )}

                {/* Stage Counts - Clickable Filters (if stages are configured) */}
                {canEdit && boardStages.length > 0 && (
                  <div className="stage-counts" style={{ marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>Stages</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {boardStages.map((stage) => (
                        <div
                          key={stage.id}
                          className={`status-count-item ${selectedStageFilter === stage.id.toString() ? 'active' : ''}`}
                          onClick={() => {
                            if (selectedStageFilter === stage.id.toString()) {
                              setSelectedStageFilter(null);
                            } else {
                              setSelectedStageFilter(stage.id.toString());
                              setSelectedStatusFilter(null); // Clear status filter when selecting stage
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="status-name">{stage.stage_name}</span>
                          <span className="status-number">{stageCounts[stage.id] || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Compact Add Form */}
              <div className="add-submission-section">
                <div className="add-submission-buttons">
                  <button 
                    type="button" 
                    className="toggle-add-form-btn"
                    onClick={() => setShowAddForm(!showAddForm)}
                  >
                    {showAddForm ? '−' : '+'} Add New Task
                  </button>
                  <button 
                    type="button" 
                    className="add-requester-btn"
                    onClick={() => setShowAddRequesterModal(true)}
                    title="Add new requester"
                  >
                    + Requester
                  </button>
                </div>
                
                {showAddForm && canEditCurrentBoard && (
                  <form onSubmit={(e) => { e.preventDefault(); addNewTask(e); }} className="compact-add-form">
                    <div className="compact-form-row">
                      <input
                        type="text"
                        placeholder="Title *"
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        onKeyPress={handleTitleKeyPress}
                        required
                        className="compact-input"
                      />
                      <select
                        value={newTask.type || ""}
                        onChange={(e) => setNewTask({ ...newTask, type: e.target.value || null })}
                        className="compact-select"
                      >
                        <option value="">Type</option>
                        {taskTypes.map((type) => (
                          <option key={type.id} value={type.type}>
                            {type.type}
                          </option>
                        ))}
                      </select>
                      <select
                        value={newTask.priority || ""}
                        onChange={(e) => setNewTask({ ...newTask, priority: e.target.value || null })}
                        className="compact-select"
                      >
                        <option value="">Priority</option>
                        {priorities.map((priority) => (
                          <option key={priority.id} value={priority.priorityLevel}>
                            {priority.priorityLevel}
                          </option>
                        ))}
                      </select>
                      <select
                        value={newTask.status || ""}
                        onChange={(e) => setNewTask({ ...newTask, status: e.target.value || null })}
                        className="compact-select"
                      >
                        <option value="">Status</option>
                        {statuses.map((status) => (
                          <option key={status.id} value={status.status}>
                            {status.status}
                          </option>
                        ))}
                      </select>
                      <select
                        value={newTask.requester || ""}
                        onChange={(e) => {
                          const selectedPerson = people.find(p => p.name === e.target.value);
                          setNewTask({ 
                            ...newTask, 
                            requester: e.target.value || null,
                            email: selectedPerson?.emailAddress || null
                          });
                        }}
                        className="compact-select"
                      >
                        <option value="">Requester</option>
                        {people.map((person) => (
                          <option key={person.id} value={person.name}>
                            {person.name}
                          </option>
                        ))}
                      </select>
                      <button type="submit" className="compact-submit-btn">Add</button>
                    </div>
                  </form>
                )}

                {/* Add Requester Modal */}
                {showAddRequesterModal && (
                  <div className="modal-overlay" onClick={() => setShowAddRequesterModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h3>Add New Requester</h3>
                        <button 
                          className="modal-close"
                          onClick={() => {
                            setShowAddRequesterModal(false);
                            setRequesterError("");
                            setNewRequester({ name: "", email: "" });
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <form onSubmit={handleAddRequester} className="requester-form">
                        <div className="form-field">
                          <label htmlFor="requester-name">Name *</label>
                          <input
                            type="text"
                            id="requester-name"
                            value={newRequester.name}
                            onChange={(e) => {
                              setNewRequester({ ...newRequester, name: e.target.value });
                              setRequesterError("");
                            }}
                            required
                            className="modal-input"
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="requester-email">Email *</label>
                          <input
                            type="email"
                            id="requester-email"
                            value={newRequester.email}
                            onChange={(e) => {
                              setNewRequester({ ...newRequester, email: e.target.value });
                              setRequesterError("");
                            }}
                            required
                            className="modal-input"
                          />
                        </div>
                        {requesterError && (
                          <div className="error-message">{requesterError}</div>
                        )}
                        <div className="modal-actions">
                          <button 
                            type="button" 
                            className="cancel-btn"
                            onClick={() => {
                              setShowAddRequesterModal(false);
                              setRequesterError("");
                              setNewRequester({ name: "", email: "" });
                            }}
                          >
                            Cancel
                          </button>
                          <button type="submit" className="submit-btn">Add Requester</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>

              <div className="board-content">
                {/* Burndown Chart View */}
                {boardSettings.burndown_chart_enabled && activeSprint && showBurndownChart && (
                  <BurndownChart 
                    sprint={activeSprint} 
                    sprintTasks={sprintTasks}
                    planningMethod={activeSprint.planning_method || 'hours'}
                    currentUserId={user?.id}
                    hasAdminAccess={hasBoardAdminAccess}
                  />
                )}

                {/* Task Table View */}
                {filteredTasks.length === 0 ? (
                  <p className="placeholder-text">
                    {searchQuery ? 'No tasks found matching your search.' : 'No tasks assigned to this team yet.'}
                  </p>
                ) : (
                  <table className="calendar-table">
                    <thead>
                      <tr>
                        {(boardSettings.visible_columns || ['title', 'type', 'priority', 'status', 'requester', 'team']).map((column) => (
                          <th key={column} style={{ textTransform: 'capitalize' }}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.map((task) => {
                        const visibleColumns = boardSettings.visible_columns || ['title', 'type', 'priority', 'status', 'requester', 'team'];
                        // Render cells in the same order as visibleColumns
                        const renderCell = (column) => {
                          switch (column) {
                            case 'title':
                              return (
                                <td key="title">
                                  <Tooltip text={task.title} delay={500}>
                                    <span className="task-title-cell">{task.title}</span>
                                  </Tooltip>
                                </td>
                              );
                            case 'type':
                              return (
                                <td key="type" onClick={(e) => e.stopPropagation()}>
                                  <select
                                    value={task.type || ''}
                                    onChange={(e) => handleFieldUpdate(task.id, 'type', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="table-dropdown"
                                    disabled={!canEditCurrentBoard}
                                  >
                                  <option value="">-</option>
                                  {[...taskTypes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((type) => (
                                    <option key={type.id} value={type.type}>
                                      {type.type}
                                    </option>
                                  ))}
                                  </select>
                                </td>
                              );
                            case 'priority':
                              return (
                                <td key="priority" onClick={(e) => e.stopPropagation()}>
                                  <select
                                    value={task.priority || ''}
                                    onChange={(e) => handleFieldUpdate(task.id, 'priority', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="table-dropdown"
                                    disabled={!canEditCurrentBoard}
                                  >
                                    <option value="">-</option>
                                    {priorities.map((priority) => (
                                      <option key={priority.id} value={priority.priorityLevel}>
                                        {priority.priorityLevel}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              );
                            case 'status':
                              // If board has stages configured, show stages instead of statuses
                              if (boardStages.length > 0) {
                                const currentStageId = getCurrentStageForTask(task);
                                return (
                                  <td key="status" onClick={(e) => e.stopPropagation()}>
                                    <select
                                      value={currentStageId || ''}
                                      onChange={(e) => handleFieldUpdate(task.id, 'stage', e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="table-dropdown"
                                      disabled={!canEditCurrentBoard}
                                    >
                                      <option value="">-</option>
                                      {boardStages.map((stage) => (
                                        <option key={stage.id} value={stage.id}>
                                          {stage.stage_name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                );
                              } else {
                                // Fallback to statuses if no stages configured
                                return (
                                  <td key="status" onClick={(e) => e.stopPropagation()}>
                                    <select
                                      value={task.status || ''}
                                      onChange={(e) => handleFieldUpdate(task.id, 'status', e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="table-dropdown"
                                      disabled={!canEditCurrentBoard}
                                    >
                                      <option value="">-</option>
                                      {statuses.map((status) => (
                                        <option key={status.id} value={status.status}>
                                          {status.status}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                );
                              }
                            case 'requester':
                              return (
                                <td key="requester" onClick={(e) => e.stopPropagation()}>
                                  <select
                                    value={task.personName || task.requester || ''}
                                    onChange={(e) => handleFieldUpdate(task.id, 'requester', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="table-dropdown"
                                    disabled={!canEditCurrentBoard}
                                  >
                                    <option value="">-</option>
                                    {people.map((person) => (
                                      <option key={person.id} value={person.name}>
                                        {person.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              );
                            case 'team':
                              return (
                                <td key="team" onClick={(e) => e.stopPropagation()}>
                                  <select
                                    value={task.teamName || task.team || ''}
                                    onChange={(e) => handleFieldUpdate(task.id, 'team', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="table-dropdown"
                                    disabled={!canEditCurrentBoard}
                                  >
                                    <option value="">-</option>
                                    {teams.map((team) => (
                                      <option key={team.id} value={team.name}>
                                        {team.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              );
                            case 'sprint':
                              return (
                                <td key="sprint" onClick={(e) => e.stopPropagation()}>
                                  <select
                                    value={taskSprints[task.id] || ''}
                                    onChange={(e) => handleSprintChange(task.id, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="table-dropdown"
                                    disabled={!canEditCurrentBoard}
                                  >
                                    <option value="">-</option>
                                    {sprints.map((sprint) => (
                                      <option key={sprint.id} value={sprint.id}>
                                        {sprint.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              );
                            default:
                              return null;
                          }
                        };

                        return (
                          <tr 
                            key={task.id}
                            onClick={(e) => {
                              if (e.target.tagName !== 'SELECT') {
                                setSelectedTask(task);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {visibleColumns.map(column => renderCell(column))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
      
      {/* Task Modal */}
      {selectedTask && (
        <TaskModal 
          task={selectedTask} 
          onClose={() => setSelectedTask(null)}
          onUpdate={(updatedTask) => {
            setTasks(tasks.map(t => 
              t.id === updatedTask.id ? updatedTask : t
            ));
            setSelectedTask(updatedTask);
            // Refresh sprint tasks if there's an active sprint (assignee/status changes affect burndown)
            if (activeSprint) {
              fetchSprintTasks(activeSprint.id);
            }
            // Refresh sprint assignment for this task
            loadTaskSprints([updatedTask.id]);
          }}
          onDelete={(taskId) => {
            setTasks(tasks.filter(t => t.id !== taskId));
            setSelectedTask(null);
            // Refresh sprint tasks if there's an active sprint
            if (activeSprint) {
              fetchSprintTasks(activeSprint.id);
            }
          }}
          onSubtaskChange={() => {
            // Refresh sprint tasks when subtasks change (for burndown chart)
            if (activeSprint) {
              fetchSprintTasks(activeSprint.id);
            }
          }}
          canEdit={canEditCurrentBoard}
        />
      )}

      {/* Sprint Management Modal */}
      {showSprintModal && selectedBoard && (
        <SprintManagementModal
          boardId={selectedBoard.id}
          boardName={selectedBoard.name}
          onClose={() => setShowSprintModal(false)}
          onSprintUpdate={() => {
            // Refresh sprint data when sprint is created/updated/deleted
            if (selectedBoard?.id) {
              fetchActiveSprint(selectedBoard.id);
              fetchSprintsForBoard(selectedBoard.id);
              // Reload task sprints to reflect any changes
              const taskIds = tasks.map(t => t.id);
              loadTaskSprints(taskIds);
            }
          }}
        />
      )}
        </div>
      )}
    </div>
  );
}

